import { useCallback, useEffect, useRef, useState } from 'react'
import { useAuth } from '../lib/session'
import {
  REMINDER_KEY,
  REMINDER_TEXT,
  SNOOZE_CHOICES,
  currentPeriod,
  dueDateFor,
  easternToday,
  isReminderUser,
  shouldShow,
  type ReminderState,
} from '../lib/reminder-schedule'
import { completeReminder, loadReminderState, snoozeReminder } from '../lib/reminders'

/**
 * The monthly "Check Leaderboard" pop-up (Chris, 2026-10-12). Rules are in
 * `reminder-schedule.ts`; this only draws it and records her choice.
 *
 * Checks once a minute while the dashboard is open, and again whenever the tab
 * comes back into view, so a snooze that runs out brings it back without a
 * reload and a reminder that comes due while the page is open still appears.
 * Re-reads the database each time, so snoozing or finishing it on another
 * device is respected here too.
 *
 * Never traps her:
 *  - If the `reminder_state` table is missing (migration not run) or a read
 *    fails, nothing appears.
 *  - If a snooze/Done can't be SAVED, it still hides here for that snooze
 *    length (30 minutes for Done), so a database problem can never pin the
 *    box over her work.
 *
 * Review fixes (2026-10-12, adversarial pass 1):
 *  - A poll that was already in flight when she clicked is discarded, so a
 *    slow read can't undo a snooze/Done and flash the box back up.
 *  - Shows nothing if the date has rolled into a new cycle since the last
 *    read (midnight on a due day), until the next read catches up — so the
 *    buttons can never write to last month's row by mistake.
 *  - Focus moves into the box when it opens; labelled for screen readers.
 *
 * Pass 2: a failed save is logged; focus goes to the card (not a button) and
 * returns afterward; a failed-save hide applies only to its own month.
 */
const CHECK_EVERY_MS = 60_000
const DONE_FALLBACK_MINUTES = 30

export default function LeaderboardReminder() {
  const { appUser } = useAuth()
  const forRenee = isReminderUser(appUser)
  const [state, setState] = useState<ReminderState>(null)
  const [loaded, setLoaded] = useState(false)
  const [period, setPeriod] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [hidden, setHidden] = useState<{ period: string; until: number } | null>(null)
  const [, setTick] = useState(0)
  const writeSeq = useRef(0)
  const warned = useRef(false)
  const card = useRef<HTMLDivElement | null>(null)
  const returnFocus = useRef<HTMLElement | null>(null)

  const refresh = useCallback(async () => {
    const p = currentPeriod(easternToday(new Date()))
    setTick((t) => t + 1)
    if (!p) {
      setPeriod(null)
      return
    }
    const seq = writeSeq.current
    const { state: s, error } = await loadReminderState(REMINDER_KEY, p)
    // A snooze/Done happened while this read was in flight — its answer is stale.
    if (seq !== writeSeq.current) return
    if (error) {
      if (!warned.current) {
        warned.current = true
        console.warn('Leaderboard reminder unavailable:', error)
      }
      setLoaded(false)
      return
    }
    setPeriod(p)
    setState(s)
    setLoaded(true)
  }, [])

  useEffect(() => {
    if (!forRenee) return
    void refresh()
    const id = window.setInterval(() => void refresh(), CHECK_EVERY_MS)
    const onVisible = () => {
      if (document.visibilityState === 'visible') void refresh()
    }
    document.addEventListener('visibilitychange', onVisible)
    window.addEventListener('focus', onVisible)
    return () => {
      window.clearInterval(id)
      document.removeEventListener('visibilitychange', onVisible)
      window.removeEventListener('focus', onVisible)
    }
  }, [forRenee, refresh])

  const now = new Date()
  const livePeriod = currentPeriod(easternToday(now))
  const visible =
    forRenee &&
    loaded &&
    !!period &&
    livePeriod === period &&
    !(hidden && hidden.period === period && now.getTime() < hidden.until) &&
    shouldShow(now, state && state.period === period ? state : null)

  // Focus the card itself, not a button, so a keystroke she was in the middle
  // of typing can't press Snooze by accident. Put focus back where it was when
  // the box goes away.
  useEffect(() => {
    if (visible) {
      returnFocus.current = document.activeElement as HTMLElement | null
      card.current?.focus()
    } else if (returnFocus.current) {
      const el = returnFocus.current
      returnFocus.current = null
      if (document.contains(el)) el.focus()
    }
  }, [visible])

  if (!visible || !period) return null

  const record = async (kind: 'snooze' | 'done', minutes: number) => {
    writeSeq.current += 1
    setBusy(true)
    const userId = appUser?.id ?? null
    const { error } =
      kind === 'done'
        ? await completeReminder(REMINDER_KEY, period, userId)
        : await snoozeReminder(REMINDER_KEY, period, minutes, userId)
    writeSeq.current += 1
    setBusy(false)
    if (error) {
      // Couldn't save — still get out of her way for a while, and leave a
      // trace for whoever looks at the console.
      console.warn('Leaderboard reminder: save failed', error)
      setHidden({ period, until: Date.now() + minutes * 60_000 })
      return
    }
    setState(
      kind === 'done'
        ? { period, done_at: new Date().toISOString(), snoozed_until: null }
        : { period, done_at: null, snoozed_until: new Date(Date.now() + minutes * 60_000).toISOString() },
    )
  }

  const due = new Date(`${dueDateFor(period)}T12:00:00Z`).toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    timeZone: 'UTC',
  })

  return (
    <div className="reminder-backdrop" role="presentation">
      <div
        className="reminder-card"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="reminder-title"
        aria-describedby="reminder-desc"
        tabIndex={-1}
        ref={card}
      >
        <p className="reminder-kicker">Monthly reminder</p>
        <h2 id="reminder-title">{REMINDER_TEXT}</h2>
        <p className="reminder-sub" id="reminder-desc">
          Due {due}. This will keep coming back until you mark it done.
        </p>
        <div className="reminder-actions">
          {SNOOZE_CHOICES.map((m) => (
            <button
              key={m}
              className="secondary-btn"
              disabled={busy}
              onClick={() => void record('snooze', m)}
            >
              Remind me in {m === 60 ? '1 hour' : `${m} minutes`}
            </button>
          ))}
          <button
            className="btn reminder-done"
            disabled={busy}
            onClick={() => void record('done', DONE_FALLBACK_MINUTES)}
          >
            Done — close it out
          </button>
        </div>
      </div>
    </div>
  )
}
